import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const POLL_INTERVAL_MS = 4000;

export function XeroInvoiceIdBackfillPanel() {
  const [previewPolling, setPreviewPolling] = useState(false);
  const [applyPolling, setApplyPolling] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current);
      elapsedRef.current = null;
    }
    setPreviewPolling(false);
    setApplyPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // On mount: check for a previous apply job so returning users see
  // the persisted result (reload recovery).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.xeroBackfillJobLatest('apply');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result && !job.result.error) {
          setApplyResult(job.result);
        } else if (job.status === 'running') {
          startTimeRef.current = Date.now();
          startPolling(job._id, 'apply');
        } else if (job.status === 'failed') {
          // Show the error from the previous run so the user knows.
          setApplyResult({ _failed: true, error: job.error });
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback((id, operation) => {
    stopPolling();
    if (operation === 'preview') setPreviewPolling(true);
    else setApplyPolling(true);

    startTimeRef.current = startTimeRef.current ?? Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.xeroBackfillJobStatus(id);
        if (data.status === 'completed') {
          stopPolling();
          startTimeRef.current = null;
          // The runner returns {error: "..."} on auth/connection failure
          // without raising (so the job is "completed" with an error in the
          // result payload). Surface it the same way the old sync endpoints did.
          if (data.result?.error) {
            toast.error(data.result.error);
          } else if (operation === 'preview') {
            setPreview(data.result);
            if (data.result?.fetch_failed) {
              toast.error('Xero fetch failed. Preview cannot propose writes against an incomplete index.');
            } else {
              toast.success('Preview complete');
            }
          } else {
            setApplyResult(data.result);
            if (data.result?.fetch_failed) {
              toast.error('Xero fetch failed. Apply aborted with zero writes.');
            } else {
              toast.success(`Backfill complete: ${data.result?.written ?? 0} links written`);
            }
            // Clear preview so Apply cannot be re-clicked without a fresh preview.
            setPreview(null);
          }
        } else if (data.status === 'failed') {
          stopPolling();
          startTimeRef.current = null;
          toast.error(data.error ?? `${operation} failed`);
        }
      } catch (err) {
        stopPolling();
        startTimeRef.current = null;
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const runPreview = async () => {
    setDispatching(true);
    setPreview(null);
    setApplyResult(null);
    setElapsed(0);
    startTimeRef.current = Date.now();
    try {
      const { data } = await diagnosticsAPI.xeroBackfillPreviewStart();
      startPolling(data.job_id, 'preview');
    } catch (err) {
      startTimeRef.current = null;
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const runApply = async () => {
    if (!preview || preview.proposed_write_count === 0) return;
    if (!window.confirm(
      `This will write xero_invoice_id on ${preview.proposed_write_count} ` +
      `bros_invoices. Continue?`
    )) return;

    setDispatching(true);
    setApplyResult(null);
    setElapsed(0);
    startTimeRef.current = Date.now();
    try {
      const { data } = await diagnosticsAPI.xeroBackfillApplyStart();
      startPolling(data.job_id, 'apply');
    } catch (err) {
      startTimeRef.current = null;
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const busy = dispatching || previewPolling || applyPolling;
  const canApply = preview && preview.proposed_write_count > 0 && !preview.fetch_failed && !busy;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Xero Invoice ID Backfill</CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          Write xero_invoice_id onto bros_invoices resolved uniquely by the live Xero probe.
          Preview runs a fresh probe; Apply re-probes and writes resolved_unique only.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Button onClick={runPreview} disabled={busy} size="sm">
            {previewPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          <Button
            onClick={runApply}
            disabled={!canApply}
            size="sm"
            variant={canApply ? 'default' : 'outline'}
          >
            {applyPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {preview ? `Apply ${fmtInt(preview.proposed_write_count)} links` : 'Apply'}
          </Button>
          {(previewPolling || applyPolling) && (
            <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              {elapsed}s
            </span>
          )}
        </div>

        {/* ── Preview: fetch failed ── */}
        {preview && preview.fetch_failed && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              PREVIEW FAILED
            </Badge>
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  Xero fetch incomplete. Cannot propose writes.
                </p>
              </div>
              <p className="text-xs text-red-600/80 dark:text-red-400/80">
                Classification against a partial index would produce confident-wrong results.
                Re-run after the rate-limit window clears.
              </p>
              {preview.failed_tenants && preview.failed_tenants.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-red-600">Failed tenants:</p>
                  {preview.failed_tenants.map((ft, i) => (
                    <p key={i} className="text-[10px] text-red-600/80 font-mono pl-2">
                      {ft.tenantName}: {ft.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Generated: {preview.generated_at}
            </p>
          </div>
        )}

        {/* ── Preview result ── */}
        {preview && !preview.fetch_failed && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              PREVIEW (read-only)
            </Badge>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Unlinked</p>
                <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(preview.total_unlinked)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Proposed writes</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(preview.proposed_write_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-amber-200 dark:border-amber-800">
                <p className="text-xs text-muted-foreground">Ambiguous</p>
                <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(preview.ambiguous_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Not in Xero</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(preview.not_in_xero_count)}</p>
              </div>
            </div>

            {preview.conflict_count > 0 && (
              <p className="text-xs text-amber-600 font-medium">
                {fmtInt(preview.conflict_count)} conflict(s) skipped (existing xero_invoice_id differs from probe)
              </p>
            )}

            {/* Match-key breakdown */}
            {preview.match_key_counts && Object.keys(preview.match_key_counts).length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1">Proposed writes by match key</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(preview.match_key_counts)
                    .sort(([, a], [, b]) => b - a)
                    .map(([key, count]) => (
                      <span key={key} className="inline-flex items-center gap-1 text-xs border border-green-200 dark:border-green-800 rounded px-2 py-0.5">
                        <span className="font-mono text-green-600">{count}</span>
                        <span className="text-muted-foreground">{key}</span>
                      </span>
                    ))}
                </div>
              </div>
            )}

            {/* Status breakdown */}
            {preview.status_counts && Object.keys(preview.status_counts).length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1">Proposed writes by BR OS status</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(preview.status_counts)
                    .sort(([, a], [, b]) => b - a)
                    .map(([status, count]) => (
                      <span key={status} className="inline-flex items-center gap-1 text-xs border rounded px-2 py-0.5">
                        <span className="font-mono">{count}</span>
                        <span className="text-muted-foreground">{status}</span>
                      </span>
                    ))}
                </div>
              </div>
            )}

            {/* Proposed writes table */}
            {preview.proposed_writes && preview.proposed_writes.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Proposed writes ({preview.proposed_writes.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">bros_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">xero_invoice_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">match_key</th>
                        <th className="text-left px-2 py-1.5 font-medium">status</th>
                        <th className="text-right px-2 py-1.5 font-medium">amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.proposed_writes.map((w, i) => (
                        <tr key={w.bros_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{w.bros_id}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{w.xero_invoice_id}</td>
                          <td className="px-2 py-1">{w.match_key}</td>
                          <td className="px-2 py-1">{w.status}</td>
                          <td className="px-2 py-1 text-right font-mono">{w.total_amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {preview.xero_errors && preview.xero_errors.length > 0 && (
              <div className="border rounded-lg p-2 border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/10">
                <p className="text-xs font-medium text-red-600 mb-1">Xero fetch errors</p>
                {preview.xero_errors.map((e, i) => (
                  <p key={i} className="text-[10px] text-red-600/80 font-mono">{e}</p>
                ))}
              </div>
            )}

            <p className="text-[10px] text-muted-foreground">
              Generated: {preview.generated_at}
            </p>
          </div>
        )}

        {/* ── Apply: fetch failed ── */}
        {applyResult && applyResult.fetch_failed && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              APPLY ABORTED
            </Badge>
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  Xero fetch incomplete. Zero writes performed.
                </p>
              </div>
              <p className="text-xs text-red-600/80 dark:text-red-400/80">
                Apply aborted because the Xero index was incomplete. Writing against a
                partial index risks linking invoices to the wrong Xero record (an invoice
                matching uniquely in tenant A may also exist in the failed tenant B).
              </p>
              {applyResult.failed_tenants && applyResult.failed_tenants.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-red-600">Failed tenants:</p>
                  {applyResult.failed_tenants.map((ft, i) => (
                    <p key={i} className="text-[10px] text-red-600/80 font-mono pl-2">
                      {ft.tenantName}: {ft.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Run: {applyResult.run_id} | Generated: {applyResult.generated_at}
            </p>
          </div>
        )}

        {/* ── Apply result ── */}
        {applyResult && !applyResult._failed && !applyResult.fetch_failed && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              APPLIED
            </Badge>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Written</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(applyResult.written)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Conflicts skipped</p>
                <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(applyResult.skipped_conflict)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Ambiguous</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(applyResult.ambiguous_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Not in Xero</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(applyResult.not_in_xero_count)}</p>
              </div>
            </div>

            {applyResult.re_run_scan_prompt && (
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                {applyResult.re_run_scan_prompt}
              </p>
            )}

            {applyResult.written_samples && applyResult.written_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Written samples ({applyResult.written_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">bros_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">xero_invoice_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">match_key</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyResult.written_samples.map((w, i) => (
                        <tr key={w.bros_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{w.bros_id}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{w.xero_invoice_id}</td>
                          <td className="px-2 py-1">{w.match_key}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Run: {applyResult.run_id} | Generated: {applyResult.generated_at}
            </p>
          </div>
        )}

        {/* ── Failed apply from previous session ── */}
        {applyResult && applyResult._failed && (
          <div className="space-y-2">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              PREVIOUS APPLY FAILED
            </Badge>
            <p className="text-xs text-red-600">{applyResult.error}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
